import axios from "axios";
import type { Action } from "@reduxjs/toolkit";
import type { ThunkAction } from "redux-thunk";
import type { AppState, AppThunk } from "@Store/index";
import { getMetaSuccess } from "@App/appReducer";
import getBlogCollectionUrl from "@Pages/Blog/utils/getBlogCollectionUrl";
import {
  ALL_BLOGS_PAGE_SIZE,
  ALL_BLOGS_SCOPE,
  LATEST_TAB_ID,
  getListingKey,
  getStageTab,
} from "@Pages/Blog/BlogHomeRevamp/settings";
import { loadFirstListingPage } from "./fetchBlogListingPage";
import { pageSettingsLoaded, type AllBlogsPageSettings } from "./blogAllPostsReducer";

const META_TITLE = "All Blogs - Student Housing Guides and Articles | Amber";
const META_DESCRIPTION =
  "Housing guides, renting advice and student life stories from 20+ countries.";

const BLOG_PAGE_SETTINGS_COLLECTION = "blog-page-settings";

type BlogPageSettingsResponse = {
  docs?: { allBlogs?: AllBlogsPageSettings | null }[];
};

const loadAllBlogsPageSettings =
  (): ThunkAction<Promise<AllBlogsPageSettings | null>, AppState, unknown, Action<string>> =>
  async (dispatch, getState) => {
    const { pageSettings, pageSettingsLoaded: isLoaded } = getState().blogAllPostsData;
    if (isLoaded) return pageSettings;
    let settings: AllBlogsPageSettings | null = null;
    try {
      const { data } = await axios.get<BlogPageSettingsResponse>(
        `${getBlogCollectionUrl(BLOG_PAGE_SETTINGS_COLLECTION)}?limit=1`,
      );
      settings = data?.docs?.[0]?.allBlogs ?? null;
    } catch {
      settings = null;
    }
    dispatch(pageSettingsLoaded(settings));
    return settings;
  };

const getBlogAllPostsData =
  ({ stage, staticContext }: { stage?: string | null; staticContext?: any } = {}): AppThunk =>
  async (dispatch, getState) => {
    const tab = getStageTab(stage);
    const key = getListingKey(ALL_BLOGS_SCOPE, tab.id);

    const [settings, isLoaded] = await Promise.all([
      dispatch(loadAllBlogsPageSettings()),
      dispatch(
        loadFirstListingPage(ALL_BLOGS_SCOPE, {
          key,
          pageSize: ALL_BLOGS_PAGE_SIZE,
          categorySlug: tab.categorySlug,
          stageSlug: tab.stageSlug,
        }),
      ),
    ]);

    const meta: any = {
      title: META_TITLE,
      description: settings?.description?.trim() || META_DESCRIPTION,
      keywords: ["student accommodation", "student housing guides", "student blog"],
      canonical_url_suffix: "/blog/latest",
    };
    dispatch(getMetaSuccess(meta));

    if (!staticContext || isLoaded === null) return;

    if (!isLoaded) {
      staticContext.statusCode = 500;
    } else if (
      tab.id === LATEST_TAB_ID &&
      getState().blogAllPostsData.listings[key]?.posts.length === 0
    ) {
      staticContext.statusCode = 404;
    }
  };

export default getBlogAllPostsData;
